import { styled } from 'goober';
import { useEffect, useState } from 'preact/hooks';

import ChevronDownIcon from '~/assets/chevron-down.svg?react';
import WorknetIcon from '~/assets/worknet-icon.svg?react';
import { sizeToPixelNumber } from '~/utils/utils';

import { BTN_HEIGHT } from './FabButton';

import type { WidgetZIndexLayers } from '../utils/zIndexLayers';
import type { WorknetWidgetConfig } from '@worknet/models';

const GAP = 12;

type Side = WorknetWidgetConfig['styles']['side'];

type WindowStateProps = {
  opening?: boolean;
  closing?: boolean;
  isMobile: boolean;
  isVisible?: boolean;
};

type ContainerProps = WindowStateProps & {
  width: WorknetWidgetConfig['styles']['width'];
  side: Side;
  topOffsetNumber: number;
  zIndex: number;
};

export type ChatWindowProps = WindowStateProps & {
  onClose?: () => void;
  widgetConfig: WorknetWidgetConfig;
  chatUrl: string;
  zIndexLayers?: WidgetZIndexLayers;
};

const PopupContainer = styled('div')<ContainerProps>`
  position: absolute;
  bottom: ${BTN_HEIGHT + GAP}px;
  ${(p) => (p.side === 'left' ? 'left: 0;' : 'right: 0;')}
  width: min(100vw,  ${(p) => p.width});
  height: calc(100vh - ${BTN_HEIGHT + GAP}px - 40px - ${(p) => p.topOffsetNumber ?? 0}px);
  background: white;
  color: #111;
  border-radius: 12px;
  box-shadow:
    rgba(50, 50, 93, 0.25) 0 6px 12px -2px,
    rgba(0, 0, 0, 0.3) 0 3px 7px -3px;
  overflow: hidden;
  z-index: ${(p) => p.zIndex};
  display: flex;
  flex-direction: column;
  will-change: transform, opacity;
  transform-origin: ${(p) => (p.side === 'left' ? '0% 100%' : '100% 100%')};

  @media (max-width: 768px) {
    position: fixed;
    right: 0;
    bottom: 0;
    width: 100vw;
    width: 100dvw;
    height: 100vh;
    height: 100dvh;
    border-radius: 0;
  }
  animation: ${(p) =>
    p.opening
      ? 'worknet-scale-in 380ms cubic-bezier(0.4, 0, 0.2, 1) both'
      : p.closing
        ? 'worknet-scale-out 320ms cubic-bezier(0.4, 0, 0.2, 1) both'
        : 'none'};

  ${(p) =>
    p.isVisible === false &&
    `
      visibility: hidden;
      opacity: 0;
      pointer-events: none;
      transform: translateY(8px) scale(0.98);
      animation: none;
    `}

  @keyframes worknet-scale-in {
    0% {
      opacity: 0;
      transform: translateY(8px) scale(0);
    }
    60% {
      opacity: 1;
    }
    100% {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
  @keyframes worknet-scale-out {
    0% {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
    100% {
      opacity: 0;
      transform: translateY(8px) scale(0);
    }
  }
`;

// const DRAWER_MARGINS = 10;
const DrawerContainer = styled('div')<ContainerProps>`
  position: fixed;
  ${(p) => (p.side === 'left' ? 'left:0;' : 'right:0;')}
  height: calc(100dvh - ${(p) =>
    p.topOffsetNumber && !p.isMobile
      ? p.topOffsetNumber
      : 0}px - env(safe-area-inset-bottom, 200px));
  width: min(100vw, ${(p) => p.width});
  bottom: 0px;
  background: white;
  color: #111;
  box-shadow:
    rgba(50, 50, 93, 0.25) 0px 6px 12px -2px,
    rgba(0, 0, 0, 0.3) 0px 3px 7px -3px;
  overflow: hidden;
  z-index: ${(p) => p.zIndex};
  display: flex;
  flex-direction: column;
  will-change: transform, opacity;
  transform-origin: ${(p) => {
    const buttonCenterFromBottom = -(GAP + BTN_HEIGHT / 2);
    return p.side === 'left'
      ? `${BTN_HEIGHT / 2}px calc(100% + ${buttonCenterFromBottom}px)`
      : `calc(100% - ${BTN_HEIGHT / 2}px) calc(100% + ${buttonCenterFromBottom}px)`;
  }};
  @media (max-width: 768px) {
    width: calc(100vw - 15px);
    height: calc(100dvh - env(safe-area-inset-bottom, 200px));
    top: 0;
    ${(p) => (p.side === 'left' ? 'left:0;' : 'right:0;')}
    border-radius: 0;
  }
  animation: ${(p) =>
    p.opening
      ? p.side === 'left'
        ? 'openDrawer 360ms cubic-bezier(0.4, 0, 0.2, 1) both'
        : 'openDrawer 360ms cubic-bezier(0.4, 0, 0.2, 1) both'
      : p.closing
        ? p.side === 'left'
          ? 'closeDrawer 300ms cubic-bezier(0.4, 0, 0.2, 1) both'
          : 'closeDrawer 300ms cubic-bezier(0.4, 0, 0.2, 1) both'
        : 'none'};

  @keyframes openDrawer {
    0% {
      transform: scale(0);
    }
    80% {
      opacity: 1;
    }
    100% {
      transform: scale(1);
    }
  }
  @keyframes closeDrawer {
    0% {
      transform: scale(1);
      opacity: 1;
    }
    100% {
      transform: scale(0);
    }
  }
  /* Keep mounted but hidden when not visible */
  ${(p) =>
    p.isVisible === false &&
    `
      visibility: hidden;
      opacity: 0;
      pointer-events: none;
      transform: scale(0.98);
      animation: none;
    `}
`;

const Header = styled('div')`
  background: var(--clr-primary);
  color: var(--clr-secondary);
  padding: 0 !important;
  padding: 14px 17px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const HeaderText = styled('h2')`
  font-size: 15px;
  font-weight: 600;
  padding: 0 !important;
  margin: 0 !important;
`;

const CloseButton = styled('button')`
  border: 0;
  background: transparent;
  color: inherit;
  padding: 0 !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const Body = styled('div')`
  padding: 0;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: green;
`;

const FrameWrapper = styled('div')`
  position: relative;
  width: 100%;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  border-top: 1px solid var(--borders-color, #e5e7eb);
  border-bottom: 1px solid var(--borders-color, #e5e7eb);
`;

const CopilotFrame = styled('iframe')`
  display: block;
  width: 100%;
  height: 100%;
  border: 0;
  background: #fff;
`;

const Footer = styled('div')`
  display: flex;
  flex-direction: column;
  margin-top: auto;
  justify-content: center;
  background-color: var(--footer-bg-color);
  align-items: center;
  color: #718096;
  font-family: var(--primary-font);
  font-weight: 300;
  font-size: 14px;
  border-top: 1px solid var(--borders-color);
  border-top: 1px solid #cccccc;
  padding: 15px 2px;
  width: 100%;
  max-height: 48px;
  order: 1;

  a {
    text-decoration: none;
    font-weight: 400;
    color: #718096;
  }
`;

const FooterText = styled('span')`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 4px;
  a {
    margin-top: 5px;
  }
`;

export function ChatWindow({
  opening,
  closing,
  onClose,
  widgetConfig,
  // widgetAppParams,
  chatUrl,
  isMobile,
  isVisible,
  zIndexLayers,
}: ChatWindowProps) {
  const isDrawer = widgetConfig.chat.isDrawer;
  const ContainerComponent = widgetConfig.chat.isDrawer ? DrawerContainer : PopupContainer;
  const topOffsetNumber = sizeToPixelNumber(widgetConfig.styles.topOffset, 'height');
  const width = `${sizeToPixelNumber(widgetConfig.styles.width, 'width')}px`;
  const footer = widgetConfig.chat.footer;

  const [visibleUrl, setVisibleUrl] = useState<string>(); // Only load the iframe if visible and url

  useEffect(() => {
    if (visibleUrl && visibleUrl === chatUrl) {
      return;
    }
    if (isVisible) {
      setVisibleUrl(chatUrl);
    }
  }, [visibleUrl, chatUrl, isVisible]);

  return (
    <ContainerComponent
      side={widgetConfig.styles.side}
      width={width}
      topOffsetNumber={topOffsetNumber}
      zIndex={zIndexLayers?.chatWindow ?? widgetConfig.styles.zIndex}
      opening={opening}
      closing={closing}
      isMobile={isMobile}
      isVisible={isVisible}
    >
      <Header>
        <HeaderText>{widgetConfig.chat.header.text}</HeaderText>
        {(isDrawer || (!isDrawer && isMobile)) && (
          <CloseButton type="button" aria-label="Close chat" onClick={onClose} title="Close">
            <ChevronDownIcon width={16} height={11} />
          </CloseButton>
        )}
      </Header>
      <Body>
        <FrameWrapper>
          <CopilotFrame
            src={visibleUrl}
            title="Worknet Copilot"
            allow="clipboard-read; clipboard-write; microphone; camera; midi; encrypted-media; autoplay; fullscreen; display-capture"
            sandbox="allow-same-origin allow-forms allow-scripts allow-popups allow-popups-to-escape-sandbox allow-modals allow-downloads allow-top-navigation-by-user-activation"
          />
        </FrameWrapper>
      </Body>
      {footer.isHidden !== true && (
        <Footer>
          <FooterText>
            <span>{footer.text}</span>
            <a
              href="https://worknet.ai"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Worknet.ai"
            >
              <WorknetIcon width={60} height={16.67} />
            </a>
          </FooterText>
          {footer.privacyPolicyText && footer.privacyPolicyUrl && (
            <>
              <a href={footer.privacyPolicyUrl} target="_blank" rel="noopener noreferrer">
                {footer.privacyPolicyText}
              </a>
            </>
          )}
        </Footer>
      )}
    </ContainerComponent>
  );
}
